// Phase 8c — rate-modifier admin schemas (operator-only).
import { z } from 'zod';
import { RateModifierApplyToSchema, RateModifierFormulaSchema, ShiftSettingSchema, } from './enums.schema.js';
import { RateIsoDateSchema, RateLeadTimeOverrideSchema, RateMoneyAmountSchema, RateReasonSchema, } from './_shared.schema.js';
const ModifierCommonFields = {
    code: z.string().min(1).max(50).regex(/^[A-Z][A-Z0-9_]*$/, 'Code must be UPPER_SNAKE_CASE'),
    applyTo: RateModifierApplyToSchema,
    formula: RateModifierFormulaSchema,
    amount: RateMoneyAmountSchema,
    appliesToRoleIds: z.array(z.string().uuid()).default([]),
    appliesToSettings: z.array(ShiftSettingSchema).default([]),
    lastMinuteThresholdHours: z.number().int().positive().nullable(),
};
export const AdminRateModifierCreateRequestSchema = z
    .object({
    ...ModifierCommonFields,
    effectiveFrom: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .and(RateLeadTimeOverrideSchema);
export const AdminRateModifierSupersedeRequestSchema = z
    .object({
    newAmount: RateMoneyAmountSchema,
    newApplyTo: RateModifierApplyToSchema.optional(),
    newFormula: RateModifierFormulaSchema.optional(),
    newAppliesToRoleIds: z.array(z.string().uuid()).optional(),
    newAppliesToSettings: z.array(ShiftSettingSchema).optional(),
    newLastMinuteThresholdHours: z.number().int().positive().nullable().optional(),
    effectiveFrom: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .and(RateLeadTimeOverrideSchema);
export const AdminRateModifierDeprecateRequestSchema = z
    .object({
    effectiveTo: RateIsoDateSchema,
    reason: RateReasonSchema,
})
    .and(RateLeadTimeOverrideSchema);
// v0/rate-provenance Phase 4 — in-place edit of an UNUSED rate modifier. VALUE
// fields only: `code` (identity) + the effective dates stay frozen. The
// LASTMIN-requires-threshold rule is re-checked service-side against the frozen
// code. No reason / lead-time / audit. Server-gated to inUse=false.
export const AdminRateModifierEditRequestSchema = z.object({
    applyTo: RateModifierApplyToSchema,
    formula: RateModifierFormulaSchema,
    amount: RateMoneyAmountSchema,
    appliesToRoleIds: z.array(z.string().uuid()).default([]),
    appliesToSettings: z.array(ShiftSettingSchema).default([]),
    lastMinuteThresholdHours: z.number().int().positive().nullable(),
});
export const AdminRateModifierResponseSchema = z.object({
    id: z.string().uuid(),
    code: z.string(),
    applyTo: RateModifierApplyToSchema,
    formula: RateModifierFormulaSchema,
    amount: z.string(),
    appliesToRoleIds: z.array(z.string().uuid()),
    appliesToSettings: z.array(ShiftSettingSchema),
    lastMinuteThresholdHours: z.number().int().nullable(),
    effectiveFrom: RateIsoDateSchema,
    effectiveTo: RateIsoDateSchema.nullable(),
    isCurrent: z.boolean(),
    // v0/rate-provenance — has this row priced any shift? Drives the admin panel's
    // Edit/Delete (unused) vs Supersede/Deprecate (used) action split.
    inUse: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const AdminRateModifierListQuerySchema = z.object({
    code: z.string().optional(),
    includeHistorical: z.coerce.boolean().default(false),
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().positive().max(100).default(20),
});
export const AdminRateModifierListResponseSchema = z.object({
    items: z.array(AdminRateModifierResponseSchema),
    totalCount: z.number().int().min(0),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=rate-modifier-admin.schema.js.map